import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';

/**
 * InstitutionalHero — Phase 99A light direction (product-led).
 *
 * Left: eyebrow → editorial headline → subcopy → CTAs
 * Right: transaction lifecycle panel (Intake → Deal room → Capital match →
 *        Execution) with the active stage cycling every 2.2s. Reduced-motion
 *        freezes the panel on "Capital match" by default.
 *
 * Phase 99A change: the small proof-stat tiles previously rendered below the
 * CTAs have been removed from the hero. Weak first-impression numbers were
 * undermining the headline; real proof now lives below in the Pathways,
 * Capital network loop and Selected transactions sections, plus the Platform
 * workspace section. The /api/public/stats endpoint is preserved and used
 * elsewhere (e.g. LiveStats badge).
 */

const STAGES = [
  { id: 'intake',        label: 'Intake',        desc: 'Opportunity submitted' },
  { id: 'deal-room',     label: 'Deal room',     desc: 'Documents & NDA controlled' },
  { id: 'capital-match', label: 'Capital match', desc: 'Matched lenders reviewing' },
  { id: 'execution',     label: 'Execution',     desc: 'Terms & close tracked' },
];

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// ── Lifecycle panel ────────────────────────────────────────────────────────

const LifecyclePanel = () => {
  const [active, setActive] = useState(prefersReducedMotion() ? 2 : 0);

  useEffect(() => {
    if (prefersReducedMotion()) return undefined;
    const id = setInterval(() => setActive((i) => (i + 1) % STAGES.length), 2200);
    return () => clearInterval(id);
  }, []);

  return (
    <aside
      aria-label="Illustrative transaction lifecycle"
      className="gpc-reveal is-visible"
      style={{
        background: 'var(--gpc-paper-2)',
        border: '1px solid var(--gpc-line)',
        borderRadius: '14px',
        padding: '22px',
      }}
      data-testid="hero-lifecycle-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <span
          style={{
            fontFamily: 'Space Grotesk, system-ui, sans-serif',
            fontSize: '11px',
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'var(--gpc-ink-soft)',
          }}
        >
          Transaction lifecycle
        </span>
        <span
          style={{
            fontFamily: 'Space Grotesk, system-ui, sans-serif',
            fontSize: '10px',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'var(--gpc-ink-soft)',
            border: '1px solid rgba(140,107,46,0.35)',
            borderRadius: '20px',
            padding: '3px 10px',
          }}
        >
          Controlled
        </span>
      </div>

      <div className="flex flex-col gap-2 mt-1.5">
        {STAGES.map((s, i) => {
          const isActive = i === active;
          return (
            <div
              key={s.id}
              data-testid={`lifecycle-stage-${s.id}`}
              data-active={isActive ? 'true' : 'false'}
              className="flex items-center gap-3.5"
              style={{
                padding: '15px 14px',
                borderRadius: '9px',
                border: '1px solid ' + (isActive ? 'rgba(140,107,46,0.22)' : 'transparent'),
                background: isActive ? 'rgba(140,107,46,0.07)' : 'transparent',
                transition: 'background-color 0.4s ease, border-color 0.4s ease',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: '11px',
                  height: '11px',
                  borderRadius: '50%',
                  border: '1.5px solid ' + (isActive ? 'var(--gpc-gold)' : 'var(--gpc-ink-faint)'),
                  background: isActive ? 'var(--gpc-gold)' : 'transparent',
                  flex: '0 0 auto',
                  transition: 'background-color 0.4s ease, border-color 0.4s ease',
                }}
              />
              <div>
                <div
                  style={{
                    fontFamily: 'Space Grotesk, system-ui, sans-serif',
                    fontSize: '11px',
                    letterSpacing: '0.13em',
                    textTransform: 'uppercase',
                    color: 'var(--gpc-ink-soft)',
                    transition: 'color 0.4s ease',
                  }}
                >
                  {s.label}
                </div>
                <div
                  style={{
                    fontSize: '13.5px',
                    color: 'var(--gpc-ink)',
                    marginTop: '2px',
                  }}
                >
                  {s.desc}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </aside>
  );
};

// ── Hero ────────────────────────────────────────────────────────────────────

export const InstitutionalHero = ({ division = false }) => {
  const Heading = division ? "h2" : "h1";
  return (
    <section
      style={{ background: 'var(--gpc-paper)' }}
      id="private-capital"
      aria-label="Private Capital division"
      data-testid="institutional-hero"
    >
      <div
        className="grid"
        style={{
          maxWidth: '1180px',
          margin: '0 auto',
          padding: 'clamp(3rem, 7vw, 6rem) clamp(1.5rem, 4vw, 4rem) clamp(2.5rem, 5vw, 4rem)',
          gap: 'clamp(2rem, 5vw, 4.5rem)',
          gridTemplateColumns: 'minmax(0, 1.05fr) minmax(0, 0.95fr)',
        }}
      >
        {/* Left column */}
        <div className="hero-left">
          <p className="gpc-eyebrow gpc-reveal is-visible" data-testid="hero-eyebrow" style={{ marginBottom: '14px' }}>
            Private Capital · Institutional execution
          </p>
          <Heading
            className="gpc-reveal is-visible"
            data-testid="hero-headline"
            style={{
              fontFamily: 'Fraunces, "Playfair Display", Georgia, serif',
              fontWeight: 400,
              fontSize: 'clamp(2.5rem, 5.4vw, 4.4rem)',
              lineHeight: 1.05,
              letterSpacing: '-0.015em',
              color: 'var(--gpc-ink)',
              marginTop: 0,
              marginBottom: '6px',
            }}
          >
            Your private capital<br />
            <em style={{ color: 'var(--gpc-gold)', fontStyle: 'italic', fontWeight: 400 }}>execution desk.</em>
          </Heading>
          <p
            className="gpc-reveal is-visible"
            data-testid="hero-subcopy"
            style={{
              fontFamily: 'Inter, system-ui, sans-serif',
              fontSize: 'clamp(1rem, 1.35vw, 1.18rem)',
              color: 'var(--gpc-ink-soft)',
              maxWidth: '34em',
              marginTop: '20px',
              lineHeight: 1.6,
            }}
          >
            Source, structure, circulate and manage private credit, real-estate finance and structured transactions through one controlled, NDA-gated process — not scattered emails and lost term-sheet versions.
          </p>

          <div
            className="gpc-reveal is-visible"
            data-testid="hero-ctas"
            style={{ display: 'flex', flexWrap: 'wrap', gap: '13px', marginTop: '32px' }}
          >
            <Link to="/contact" className="gpc-cta-primary" data-testid="hero-cta-submit">
              Submit a transaction
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/for-lenders" className="gpc-cta-secondary" data-testid="hero-cta-lender">
              Request lender access
            </Link>
            <Link to="/portal/login" className="gpc-cta-secondary" data-testid="hero-cta-platform">
              Enter platform
            </Link>
          </div>
        </div>

        {/* Right column — lifecycle panel */}
        <div className="hero-right" style={{ minWidth: 0 }}>
          <LifecyclePanel />
        </div>
      </div>

      {/* Responsive: stack on narrow viewports */}
      <style>{`
        @media (max-width: 860px) {
          [data-testid="institutional-hero"] > div {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
};

export default InstitutionalHero;
